<template>
  <div>
    <project-toolbar
      :selectMonth="true"
      :selectProject="false"
      :showCreditFilter="true"
    />
    <md-content v-if="summaryData.length">
      <md-card class="card">
        <md-card-header>
          <span class="card-title">{{ L('accountsSummary') }}</span>
        </md-card-header>
        <md-card-content>
          <div class="notify-area">
            {{ L('accountNotify') }} ({{ curMonth }})
            <span
              ><strong>{{
                selectedTotalCost | formatCurrency(displayCurrency)
              }}</strong></span
            >
          </div>

          <md-field class="search-field">
            <md-input v-model="monthly.search" :placeholder="placeholder" />
            <md-button
              class="md-icon-button md-dense"
              @click="searchSummaryData(monthly.search)"
            >
              <fa-icon icon="search" />
            </md-button>
          </md-field>

          <md-table
            v-model="monthlyCost"
            :md-selected-value.sync="selected"
            :md-sort.sync="monthly.sort"
            :md-sort-order.sync="monthly.sortOrder"
            md-thin
            md-stripe
            md-fixed-header
            class="min-table monthly-table"
            @md-selected="selectedAccount"
          >
            <md-table-row
              slot="md-table-row"
              slot-scope="{ item }"
              md-selectable="multiple"
              md-auto-select
            >
              <md-table-cell
                :md-label="L('account')"
                md-sort-by="account_name"
                :style="`width: ${accountSummaryTableWidths[0]}%`"
              >
                {{ `${item.account_name}(${item.account_id})` }}
              </md-table-cell>
              <md-table-cell
                v-if="showRemainingBalance"
                :md-label="`(${preMonthLastDay}) Remaining Balance`"
                md-sort-by="remaining_balance"
                md-numeric
                :style="`width: ${accountSummaryTableWidths[1]}%`"
              >
                <span class="table-cell-span">{{
                  item.remaining_balance | round2 | currency(displayCurrency)
                }}</span>
              </md-table-cell>
              <md-table-cell
                :md-label="L('ratio')"
                :style="`width: ${accountSummaryTableWidths[2]}%`"
              >
                <span class="table-cell-span">{{
                  ratioCalc(item.this_month_cost)
                }}</span>
              </md-table-cell>
              <md-table-cell
                :md-label="L('thisMonthCost')"
                md-sort-by="this_month_cost"
                md-numeric
                :style="`width: ${accountSummaryTableWidths[3]}%`"
              >
                <span class="table-cell-span">{{
                  item.this_month_cost | formatCurrency(displayCurrency)
                }}</span>
              </md-table-cell>
              <md-table-cell
                :md-label="L('lastMonthCost')"
                md-sort-by="last_month_cost"
                md-numeric
                :style="`width: ${accountSummaryTableWidths[4]}%`"
              >
                <span class="table-cell-span">{{
                  item.last_month_cost | formatCurrency(displayCurrency)
                }}</span>
              </md-table-cell>
            </md-table-row>
          </md-table>

          <md-table class="monthly-table" md-thin>
            <md-table-row>
              <md-table-cell
                :style="`width: ${accountSummaryTableWidths[0]}%; padding-left: 55px;`"
                >{{ L('totalCost') }}</md-table-cell
              >
              <md-table-cell
                v-if="showRemainingBalance"
                md-numeric
                :style="`width: ${accountSummaryTableWidths[1]}%`"
              >
                <span class="table-cell-span">{{
                  remainingBalanceTotalCost | round2 | currency(displayCurrency)
                }}</span>
              </md-table-cell>
              <md-table-cell
                :style="`width: ${accountSummaryTableWidths[2]}%`"
              />
              <md-table-cell
                md-numeric
                :style="`width: ${accountSummaryTableWidths[3]}%`"
              >
                <span class="table-cell-span">{{
                  thisMonthTotalCost | formatCurrency(displayCurrency)
                }}</span>
              </md-table-cell>
              <md-table-cell
                md-numeric
                :style="`width: ${accountSummaryTableWidths[4]}%`"
              >
                <span class="table-cell-span">{{
                  lastMonthTotalCost | formatCurrency(displayCurrency)
                }}</span>
              </md-table-cell>
            </md-table-row>
          </md-table>
        </md-card-content>
      </md-card>

      <md-card class="card">
        <md-card-header>
          <span class="card-title">{{ L('dailyCost') }}</span>
          <md-button
            class="md-primary md-dense download-btn"
            @click="download('DailyCost')"
            >{{ L('download') }}</md-button
          >
        </md-card-header>
        <md-card-content>
          <GChart
            type="LineChart"
            :data="dailyCost"
            :options="dailyChartOptions"
          />
        </md-card-content>
      </md-card>

      <md-card class="card">
        <md-card-header>
          <span class="card-title">{{ L('monthlyCost') }}</span>
          <md-button
            class="md-primary md-dense download-btn"
            @click="download('MonthlyCost')"
            >{{ L('download') }}</md-button
          >
        </md-card-header>
        <md-card-content>
          <GChart
            type="ColumnChart"
            :data="annualCost"
            :options="monthlyChartOptions"
          />
        </md-card-content>
      </md-card>

      <md-card class="card">
        <CostShare
          v-if="costByService"
          titleKey="costByService"
          nameKey="service"
          :data="costByService"
          :currency="displayCurrency"
          @click="openDetailPopup"
          class="md-layout-item"
        />
      </md-card>

      <md-card class="card">
        <CostShare
          v-if="costByRegion"
          titleKey="costByRegion"
          nameKey="region"
          :data="costByRegion"
          :currency="displayCurrency"
          @click="openDetailPopup"
          class="md-layout-item"
        />
      </md-card>

      <md-card class="card">
        <CostShare
          v-if="costByCountry"
          titleKey="costByCountry"
          nameKey="country"
          :data="costByCountry"
          :currency="displayCurrency"
          @click="openDetailPopup"
          class="md-layout-item"
        />
      </md-card>
    </md-content>

    <!-- detail information dialog -->
    <md-dialog
      v-if="detailDialog.show"
      :md-active.sync="detailDialog.show"
      class="md-dialog-big"
    >
      <md-dialog-title>{{ detailDialog.title }}</md-dialog-title>

      <md-dialog-content>
        <md-table
          v-model="detailDialog.list"
          md-fixed-header
          md-thin
          md-stripe
          class="cost-table"
        >
          <md-table-row slot="md-table-row" slot-scope="{ item }">
            <md-table-cell :md-label="L('account')"
              >{{ item.account_name }}({{ item.account_id }})</md-table-cell
            >
            <md-table-cell :md-label="L('cost')" md-numeric>{{
              item.costs.cost | currency(displayCurrency)
            }}</md-table-cell>
            <md-table-cell
              v-if="!hasCreditKind"
              :md-label="L('credit')"
              md-numeric
              >{{
                item.costs.credit | currency(displayCurrency)
              }}</md-table-cell
            >
            <md-table-cell
              v-if="hasCreditKind"
              :md-label="L('discount')"
              md-numeric
              >{{
                item.costs.discount | currency(displayCurrency)
              }}</md-table-cell
            >
            <md-table-cell
              v-if="hasCreditKind"
              :md-label="L('promotion')"
              md-numeric
              >{{
                item.costs.others | currency(displayCurrency)
              }}</md-table-cell
            >
            <md-table-cell
              v-if="hasCreditKind"
              :md-label="L('finalCost')"
              md-numeric
              >{{
                item.costs.finalCost | currency(displayCurrency)
              }}</md-table-cell
            >
          </md-table-row>
        </md-table>
      </md-dialog-content>

      <md-dialog-actions>
        <md-button class="md-primary" @click="detailDialog.show = false">{{
          L('close')
        }}</md-button>
      </md-dialog-actions>
    </md-dialog>
  </div>
</template>

<script>
import { mapState } from 'vuex'
import api from '@/api'
import ProjectToolbar from '@/views/billing/ProjectToolbar'
import { convertCosts, makeForecastData, addBarToMonth } from '@/lib/utils'
import { getDaysInMonth, getLastDay } from '@/lib/dateutil'
import { getExchangeRate } from '@/lib/exchangeRate'
import { GChart } from 'vue-google-charts'
import moment from 'moment'
import XLSX from 'xlsx'
import { capitalize } from '@/lib/textutil'
import CostShare from './Consolidated.CostShare'

export default {
  components: { ProjectToolbar, GChart, CostShare },

  data: () => ({
    summaryData: [],
    accountSummaryData: [],
    filteredSummaryData: [],

    annualData: [],
    accountAnnualData: [],
    filteredAnnualData: [],

    remainingBalanceData: [],

    monthly: {
      sort: 'this_month_cost',
      sortOrder: 'desc',
      search: '',
      searchFields: ['account'],
      searchCheck: false,
    },

    selected: [],

    accountId: {
      list: [],
    },

    detailDialog: {
      show: false,
      title: '',
      fields: [],
      data: [],
      sort: 'finalCost',
      sortOrder: 'desc',
    },

    monthlyForecastCost: 0,
  }),

  mounted() {
    this.load()
  },

  computed: {
    ...mapState({
      curMonth: (state) => state.curMonth,
      preMonth: (state) => state.preMonth,
      creditFilter: (state) => state.creditFilter,
      displayCurrency: (state) => state.displayCurrency,
      exchangeRateList: (state) => state.exchangeRateList,
      corporation: (state) => state.corporation,
      userInfo: (state) => state.userInfo,
    }),

    thisInvoiceMonth() {
      return this.curMonth.replace('-', '')
    },

    lastInvoiceMonth() {
      return this.preMonth.replace('-', '')
    },

    monthlyCost: {
      get() {
        return this.makeMonthlyData()
      },
      set() {},
    },

    dailyCost() {
      const dailyData = this.accountSummaryData.filter(
        (item) => item.kind === 'daily_cost'
      )

      const result = dailyData.reduce((arr, item) => {
        arr.push(...item.data)
        return arr
      }, [])

      const costs = result.reduce((arr, item) => {
        const check = arr.find((a) => a.date === item.date)
        const cost = this.convertCosts(item.costs, item.invoice_month).finalCost
        const month = addBarToMonth(item.invoice_month)

        if (!item.date.startsWith(month)) {
          const firstData = arr.find((a) => a.date === `${month}-01`)
          if (firstData) {
            firstData.cost += cost
          } else {
            arr.push({ date: `${month}-01`, month, cost })
          }
        } else {
          if (check) {
            check.cost += cost
          } else {
            arr.push({ date: item.date, month, cost })
          }
        }

        return arr
      }, [])

      const filtered = costs.filter((d) => d.month === d.date.slice(0, 7))
      const thisMonthDays = getDaysInMonth(this.curMonth)
      const lastMonthDays = getDaysInMonth(this.preMonth)
      const { dataPresentDays, forecasts } = makeForecastData(
        filtered,
        this.curMonth
      )

      const content = [
        [
          'Date',
          this.curMonth,
          this.preMonth,
          ...(forecasts.length > 0
            ? [`${this.curMonth} (${this.L('forecast')})`]
            : []),
        ],
      ]

      let totalForecastCost = 0

      for (
        let i = 1, end = Math.max(thisMonthDays, lastMonthDays);
        i <= end;
        i += 1
      ) {
        const day = String(i).padStart(2, '0')
        const thisVal = filtered.find(
          (c) => c.date === `${this.curMonth}-${day}`
        )
        const lastVal = filtered.find(
          (c) => c.date === `${this.preMonth}-${day}`
        )
        const forecastVal = forecasts.find(
          (c) => c.date === `${this.curMonth}-${day}`
        )
        const thisCost = thisVal ? thisVal.cost : 0
        const lastCost = lastVal ? lastVal.cost : 0
        const forecastCost = forecastVal ? forecastVal.cost : null

        totalForecastCost += forecastCost || 0

        content.push([
          day,
          i <= dataPresentDays ? thisCost : null,
          i <= lastMonthDays ? lastCost : null,
          ...(forecasts.length ? [forecastCost] : []),
        ])
      }

      this.setMonthlyForecastCost(totalForecastCost)

      return content
    },

    annualCost() {
      const array = [
        ['Month', 'Cost', `Cost(${this.L('forecastAdditionalCost')})`],
      ]
      const forecastCost = this.monthlyForecastCost || 0

      const monthlyData = this.putMonths().reduce((arr, item) => {
        const data = this.accountAnnualData.filter((i) => i.month === item)
        arr.push({ month: item, data })
        return arr
      }, [])

      monthlyData.forEach((item) => {
        const cost = item.data.reduce(
          (sum, i) =>
            sum + this.makeMonthlyCostsContent(i.data, item.month).finalCost,
          0
        )

        if (item.month === this.thisInvoiceMonth) {
          array.push([this.formatMonth(item.month), cost, forecastCost])
        } else {
          array.push([this.formatMonth(item.month), cost, null])
        }
      }, [])

      return array
    },

    remainingBalanceTotalCost() {
      if (!this.$config.feature.accountBalance) {
        return 0
      }
      return this.monthlyCost.reduce(
        (sum, item) => sum + Number(item.remaining_balance),
        0
      )
    },

    thisMonthTotalCost() {
      return this.monthlyCost.reduce(
        (sum, item) => sum + item.this_month_cost,
        0
      )
    },

    lastMonthTotalCost() {
      return this.monthlyCost.reduce(
        (sum, item) => sum + item.last_month_cost,
        0
      )
    },

    selectedTotalCost() {
      const data = this.accountSummaryData.filter(
        (item) => item.kind === 'monthly_cost'
      )
      return data.reduce(
        (sum, item) =>
          sum +
          this.makeMonthlyCostsContent(item.data, this.thisInvoiceMonth)
            .finalCost,
        0
      )
    },

    costByService() {
      return this.makeCostByKind('cost_by_service', 'service')
    },

    costByRegion() {
      return this.makeCostByKind('cost_by_region', 'region')
    },

    costByCountry() {
      return this.makeCostByKind('cost_by_country', 'country')
    },

    dailyChartOptions() {
      return {
        tooltip: { trigger: 'focus' },
        aggregationTarget: 'category',
        hAxis: { title: this.L('date') },
        vAxis: { title: `${this.L('cost')} (${this.displayCurrency})` },
        focusTarget: 'category',
        pointSize: window.innerWidth > 800 ? 8 : 4,
        legend: { position: 'top' },
        colors: ['#FF0000', '#000000', '#A6A6A6'],
        chartArea: {
          left: window.innerWidth > 800 ? 120 : 60,
          right: 10,
        },
        backgroundColor: 'none',
      }
    },

    monthlyChartOptions() {
      return {
        legend: { position: 'none' },
        isStacked: true,
        colors: ['#3366cc', '#8C8C8C'],
        chartArea: {
          left: window.innerWidth > 800 ? 120 : 60,
          right: 10,
        },
        backgroundColor: 'none',
      }
    },

    placeholder() {
      const fields = this.monthly.searchFields.map((field) => this.L(field))
      return `${this.L('searchBy')} ${fields.join(', ')}...`
    },

    hasCreditKind() {
      return this.$config.feature.creditKind
    },

    monthlyDownloadData() {
      return this.makeDownloadData(this.accountAnnualData, 'month')
    },

    dailyDownloadData() {
      const list = this.accountSummaryData.filter(
        (item) => item.kind === 'daily_cost'
      )
      const newList = JSON.parse(JSON.stringify(list))

      const result = newList.reduce((arr, item) => {
        item.data.forEach((i) => {
          const obj = {}
          obj.account_id = item.account_id
          obj.account_name = item.account_name
          obj.month = i.invoice_month
          obj.date = i.date
          obj.data = [{ invoice_month: i.invoice_month, costs: i.costs }]
          arr.push(obj)
        })
        return arr
      }, [])

      return this.makeDownloadData(result, 'date')
    },

    showRemainingBalance() {
      return (
        this.$config.feature.accountBalance &&
        this.remainingBalanceTotalCost > 0
      )
    },

    accountSummaryTableWidths() {
      if (this.showRemainingBalance) {
        return [52, 15, 3, 15, 15]
      }
      return [57, 0, 3, 20, 20]
    },

    preMonthLastDay() {
      return getLastDay(this.preMonth)
    },
  },

  watch: {
    curMonth() {
      this.clearData()
      this.load()
    },

    corporation() {
      this.clearData()
      this.load()
    },
  },

  methods: {
    load() {
      if (this.$config.feature.accountBalance) {
        let { accounts } = this.userInfo.resource
        if (this.corporation) {
          accounts = accounts.filter(
            (account) => account.corporation === this.corporation
          )
        }

        const jobs = accounts.map((account) =>
          api.promotionCredit.latestHistory(account.id, this.preMonthLastDay, {
            timezone: this.$config.timezone,
            credit_id: 'RemainingBalance',
          })
        )
        Promise.all(jobs).then((result) => {
          this.remainingBalanceData = [].concat(...result)
        })
      }

      this.putExchangeRateList().then(() => {
        Promise.all([
          api.consolidated.accountsSummary({
            month: this.thisInvoiceMonth,
            corporation: this.corporation,
          }),
          api.consolidated.accountsSummary({
            months: this.putMonths(),
            kind: 'monthly_cost',
            corporation: this.corporation,
          }),
        ]).then(([summaryList, annualList]) => {
          this.summaryData = this.findTotalData([].concat(...summaryList))
          this.annualData = this.findTotalData([].concat(...annualList))
          this.selected = this.monthlyCost
        })
      })
    },

    findTotalData(list) {
      return list.map((item) => {
        item.data = item.data.find((c) => c.project_id === '__total').data
        return item
      })
    },

    putMonths() {
      const array = []
      for (let i = 12; i >= 0; i -= 1) {
        array.push(
          moment(`${this.curMonth}-01`).subtract(i, 'M').format('YYYYMM')
        )
      }
      return array
    },

    makeMonthlyData() {
      const data = this.monthly.searchCheck
        ? this.filteredSummaryData
        : this.summaryData
      const monthlyData = data.filter((item) => item.kind === 'monthly_cost')
      const monthlyDataConvertedCost = monthlyData.map((item) => {
        item.this_month_cost = this.makeMonthlyCostsContent(
          item.data,
          this.thisInvoiceMonth
        ).finalCost
        item.last_month_cost = this.makeMonthlyCostsContent(
          item.data,
          this.lastInvoiceMonth
        ).finalCost
        item.remaining_balance = 0
        return item
      })

      let monthlyDataWithOnlyAccountBalance = []

      if (this.$config.feature.accountBalance) {
        monthlyDataWithOnlyAccountBalance = this.remainingBalanceData.reduce(
          (arr, item) => {
            const exist = monthlyDataConvertedCost.find(
              (r) => r.account_id === item.account_id
            )
            if (exist) {
              exist.remaining_balance = item.remain_amount
            } else if (item.remain_amount > 0) {
              arr.push({
                account_id: item.account_id,
                account_name: item.account_name,
                remaining_balance: item.remain_amount,
                last_month_cost: 0,
                this_month_cost: 0,
              })
            }

            return arr
          },
          []
        )
      }

      return [
        ...monthlyDataConvertedCost,
        ...monthlyDataWithOnlyAccountBalance,
      ].sort((a, b) => b.this_month_cost - a.this_month_cost)
    },

    convertCosts(costs, thisMonth) {
      let month = thisMonth || this.curMonth
      if (month.indexOf('-') === -1) {
        month = addBarToMonth(month)
      }

      return convertCosts(
        costs,
        this.displayCurrency,
        this.getExchangeRate.bind(this),
        { month },
        this.creditFilter
      )
    },

    getExchangeRate(currency, context) {
      const er = this.exchangeRateList.find(
        (e) => e.month === context.month && e.code === currency
      )
      return er ? er.send_rate : { finalCost: 0 }
    },

    async putExchangeRateList() {
      const latestMonth = moment().format('YYYY-MM')
      const months = this.putMonths().map((month) =>
        moment(month).format('YYYY-MM')
      )

      if (!months.find((e) => e === latestMonth)) {
        months.push(latestMonth)
      }

      await getExchangeRate(months)
    },

    makeMonthlyCostsContent(data, invoiceMonth) {
      const result = data.find((item) => item.invoice_month === invoiceMonth)

      let [cost, credit, discount, others, finalCost] = [0, 0, 0, 0, 0]

      if (result) {
        ;({ cost, credit, discount, others, finalCost } = this.convertCosts(
          result.costs,
          invoiceMonth
        ))
      }

      return {
        cost,
        credit,
        discount,
        others,
        finalCost,
      }
    },

    makeCostByKind(kind, type) {
      const list = this.accountSummaryData.filter((item) => item.kind === kind)

      return list
        .reduce((arr, item) => {
          item.data.forEach((data) => {
            const check = arr.find((a) => a.id === data[`${type}_id`])
            const { cost, finalCost, credit, discount, others } =
              this.convertCosts(data.costs, this.thisInvoiceMonth)

            if (check) {
              check.cost += cost
              check.finalCost += finalCost
              check.credit += credit
              check.discount += discount
              check.others += others
            } else {
              arr.push({
                id: data[`${type}_id`],
                name: data[`${type}_name`],
                cost,
                finalCost,
                credit,
                discount,
                others,
              })
            }
          })
          return arr
        }, [])
        .sort((a, b) => b.cost - a.cost)
    },

    selectedAccount() {
      const filteredList = (list) =>
        list.filter((item) => this.accountId.includes(item.account_id))
      const summaryData = this.filteredSummaryData.length
        ? this.filteredSummaryData
        : this.summaryData
      const annualData = this.filteredAnnualData.length
        ? this.filteredAnnualData
        : this.annualData

      this.accountId = this.selected.map((item) => item.account_id)
      this.accountSummaryData = filteredList(summaryData)
      this.accountAnnualData = filteredList(annualData)
    },

    formatMonth(data) {
      return `${data.substr(0, 4)}-${data.substr(4, 2)}`
    },

    ratioCalc(data) {
      const ratio = ((data.toFixed(0) / this.thisMonthTotalCost) * 100).toFixed(
        2
      )
      return data <= 0 || ratio === '0.00' ? '0%' : `${ratio}%`
    },

    searchSummaryData(search) {
      const check = (item) => item.toLowerCase().includes(search.toLowerCase())
      const filteredList = (list) =>
        list.filter(
          (item) => check(item.account_id) || check(item.account_name)
        )

      if (this.monthly.search) {
        this.monthly.searchCheck = true
      } else {
        this.monthly.searchCheck = false
      }

      this.filteredSummaryData = filteredList(this.summaryData)
      this.filteredAnnualData = filteredList(this.annualData)
      this.accountSummaryData = this.filteredSummaryData
      this.accountAnnualData = this.filteredAnnualData
      this.selected = this.monthlyCost
    },

    openDetailPopup(type, name) {
      let detail

      switch (type) {
        case 'costByService':
          detail = this.makeDetail(name, 'cost_by_service', 'service')
          break
        case 'costByRegion':
          detail = this.makeDetail(name, 'cost_by_region', 'region')
          break
        case 'costByCountry':
          detail = this.makeDetail(name, 'cost_by_country', 'country')
          break
        default:
          break
      }

      if (detail) {
        this.detailDialog = Object.assign(detail, { show: true })
      }
    },

    makeDetail(name, kind, type) {
      const summaryData = this.accountSummaryData.filter(
        (item) => item.kind === kind
      )

      const list = summaryData
        .reduce((arr, item) => {
          const findData = item.data.find((i) => i[`${type}_name`] === name)
          if (findData) {
            const costs = this.convertCosts(
              findData.costs,
              this.thisInvoiceMonth
            )
            arr.push({
              account_id: item.account_id,
              account_name: item.account_name,
              costs,
            })
          }
          return arr
        }, [])
        .sort((a, b) => b.costs.finalCost - a.costs.finalCost)

      return {
        title: `${this.L(type)}: ${name}`,
        fields: [],
        list,
      }
    },

    clearData() {
      this.monthly.search = ''
      this.monthly.searchCheck = false
      this.summaryData = []
      this.annualData = []
      this.accountSummaryData = []
      this.filteredSummaryData = []
    },

    addBarToMonth(month) {
      if (month.indexOf('-') === -1) {
        return `${month.slice(0, 4)}-${month.slice(4, 6)}`
      }
      return month
    },

    makeDownloadData(list, type) {
      const accountText = capitalize(this.$config.feature.accountText)

      const newList = JSON.parse(JSON.stringify(list))
      newList.sort(
        (a, b) =>
          a.account_id.localeCompare(b.account_id) ||
          a[type].localeCompare(b[type])
      )

      const result = newList.map((item) => {
        const obj = {}
        const { cost, credit, discount, others, finalCost } =
          this.makeMonthlyCostsContent(item.data, item.month)

        obj[`${accountText} ID`] = item.account_id
        obj[`${accountText} Name`] = item.account_name
        obj.Month = addBarToMonth(item.month)

        if (type === 'date') {
          obj.Date = item.date
        }

        obj.Currency = this.displayCurrency
        obj.Cost = cost

        if (this.hasCreditKind) {
          obj.Discount = discount
          obj.Others = others
        } else {
          obj.Credit = credit
        }

        obj['Final Cost'] = finalCost

        return obj
      })

      return result
    },

    download(type) {
      const list =
        type === 'MonthlyCost'
          ? this.monthlyDownloadData
          : this.dailyDownloadData
      const wb = XLSX.utils.book_new()
      const ws = XLSX.utils.json_to_sheet(list)

      XLSX.utils.book_append_sheet(wb, ws, 'sheet1')

      XLSX.writeFile(wb, `${type}.xlsx`)
    },

    setMonthlyForecastCost(data) {
      this.monthlyForecastCost = data
    },
  },
}
</script>

<style lang="scss" scoped>
::v-deep .min-table {
  height: 250px;
}

.md-content {
  margin: 10px 8px 8px 8px;
}

::v-deep {
  .md-card {
    margin: 18px 8px;
  }
  .md-card-content {
    font-size: 25px;

    &:last-of-type {
      padding-bottom: 16px;
    }
  }
}

.card-title {
  font-size: 17px;
}

.notify-area {
  background-color: #fff7fa;
  width: 100%;
  height: 40px;
  border-radius: 5px;
  text-align: center;
  font-size: 15px;
  line-height: 40px;
  border: 1px solid #e2d3d9;
}

.search-field {
  max-width: 300px;
  margin-right: 0 auto;
  margin-left: auto;
  margin-right: 0;
}

.monthly-table {
  ::v-deep .table-cell-span,
  ::v-deep .md-table-head:not(:first-child) {
    padding-right: 10px;
  }
}

.cost-table {
  width: 100%;
  padding-bottom: 10px;
}

.cost-table {
  ::v-deep .md-table-cell-container,
  ::v-deep .md-table-head-label {
    padding-right: 12px !important;
    padding-left: 12px !important;
  }
}

.download-btn {
  border: 1px solid var(--theme-color);
  border-radius: 5px;
  float: right;
}

.dark {
  .notify-area {
    background-color: #3c3e42;
    border: 1px solid #5f5f5f;
  }
}
</style>
